import { useMemo } from 'react';
import Link from 'next/link';
import styled, { DefaultTheme, withTheme } from 'styled-components';
import { Row, Col, Tag } from 'antd';
import TextClamp from 'react-string-clamp';

import { getOfficesByCountyPageSheetQueries } from '../../common/helpers/i18n.utils';
import { generateUrl } from '../../common/helpers/office.utils';
import { slugifySearchQuery } from '../../common/helpers/searchQuery.utils';
import useI18n from '../../common/hooks/useI18n';
import { IOffice } from '../../common/model/office.model';

type Props = {
  office: IOffice;
  className?: string;
  theme: DefaultTheme;
};

const StyledAnchor = styled.a`
  color: inherit;
  &:hover {
    color: inherit;
  }
`;

const StyledRow = styled(Row)`
  height: 100%;
`;

const StyledCol = styled(Col)`
  flex: 0 0 164px;

  @media ${(props) => props.theme.breakpoints.xs} {
    flex: 0 0 119px;
  }
`;

const BackgroundImage = styled.div<{ imageUrl: string }>`
  border-top-left-radius: ${(props) => props.theme.borderRadius};
  border-bottom-left-radius: ${(props) => props.theme.borderRadius};
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center center;
  background-image: url(${(props) => props.imageUrl});
  height: 100%;
`;

const InfoContainer = styled.div`
  padding: 16px;

  @media ${(props) => props.theme.breakpoints.xs} {
    padding-top: 8px;
    padding-bottom: 8px;
  }
`;

const Title = styled.span`
  font-weight: 500;
  font-size: 16px;
  line-height: 27px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media ${(props) => props.theme.breakpoints.lgd} {
    font-size: 14px;
  }

  @media ${(props) => props.theme.breakpoints.xs} {
    font-size: 12px;
  }
`;

const FeaturesSection = styled.div`
  margin-top: 8px;

  @media ${(props) => props.theme.breakpoints.xs} {
    margin-top: 0;
  }
`;

const FeatureInfo = styled.div<{
  $withBorderBottom?: boolean;
  $twoLines?: boolean;
}>`
  height: ${(props) => (props.$twoLines ? '65px' : '41px')};
  font-size: 14px;
  line-height: 24px;
  padding-top: 8px;
  padding-bottom: 8px;
  border-bottom: ${(props) => (props.$withBorderBottom ? '1px' : '0')} solid
    ${(props) => props.theme.colors.grey};

  @media ${(props) => props.theme.breakpoints.xs} {
    height: ${(props) => (props.$twoLines ? '49px' : '29px')};
    font-size: 12px;
    line-height: 20px;
    padding-top: 4px;
    padding-bottom: 4px;
  }
`;

const TextWithEllipsis = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SmDownHiddenCol = styled(Col)<{ $alwaysShow?: boolean }>`
  @media ${(props) => props.theme.breakpoints.smd} {
    display: ${(props) => (props.$alwaysShow ? 'block' : 'none')};
  }
`;

const StyledTag = styled(Tag)`
  font-weight: 500;
  font-size: 14px;
  line-height: 32px;
  cursor: pointer;
  width: 100%;
  text-align: center;
`;

const OfficeCard: React.FC<Props> = ({ office, className, theme }) => {
  const i18n = useI18n();

  const href = useMemo(() => generateUrl(office), [office]);
  const cityQueries = useMemo(
    () =>
      getOfficesByCountyPageSheetQueries().filter(
        (q) => q.split('/').length > 1
      ),
    []
  );
  const showCity = useMemo(
    () =>
      cityQueries.findIndex((cq) =>
        cq.includes(slugifySearchQuery(office.city))
      ) >= 0,
    [cityQueries, office.city]
  );

  return (
    <div className={className}>
      <StyledRow wrap={false}>
        <StyledCol style={{ alignSelf: 'stretch' }}>
          <Link href={href} legacyBehavior>
            <a>
              <BackgroundImage imageUrl={office.imageUrls[0]} />
            </a>
          </Link>
        </StyledCol>
        <Col flex="auto" style={{ alignSelf: 'center' }}>
          <InfoContainer>
            <Link href={href} passHref legacyBehavior>
              <StyledAnchor>
                <Title>
                  {showCity && `${office.city} - `}
                  {office.name}
                </Title>
              </StyledAnchor>
            </Link>
            <FeaturesSection>
              <FeatureInfo as="div" $withBorderBottom $twoLines>
                <TextClamp text={office.address} lines={2} />
              </FeatureInfo>
              <FeatureInfo as="div" $withBorderBottom>
                <TextWithEllipsis>{office.email}</TextWithEllipsis>
              </FeatureInfo>
              <FeatureInfo as="div">
                <Row justify="space-between">
                  <Col flex="auto">
                    <TextWithEllipsis>{office.phone}</TextWithEllipsis>
                  </Col>
                  <SmDownHiddenCol flex="100px">
                    <Link href={href} legacyBehavior>
                      <a>
                        <StyledTag color={theme.colors.secondary}>
                          {i18n.t('officeDetail.actions.knowUs')}
                        </StyledTag>
                      </a>
                    </Link>
                  </SmDownHiddenCol>
                </Row>
              </FeatureInfo>
            </FeaturesSection>
          </InfoContainer>
        </Col>
      </StyledRow>
    </div>
  );
};

export default withTheme<typeof OfficeCard>(styled(OfficeCard)`
  height: 206px;
  -webkit-box-shadow: 0px 6px 21px -4px rgba(0, 0, 0, 0.25);
  -moz-box-shadow: 0px 6px 21px -4px rgba(0, 0, 0, 0.25);
  box-shadow: 0px 6px 21px -4px rgba(0, 0, 0, 0.25);
  border-radius: ${(props) => props.theme.borderRadius};

  .ant-tag {
    display: none;
  }

  :hover {
    .ant-tag {
      display: block;
    }
  }

  @media ${(props) => props.theme.breakpoints.xs} {
    height: 150px;
  }
`);
